import ApiService from "@/core/services/ApiService";
import JwtService from "@/core/services/JwtService";
import {Actions, Mutations} from "@/store/enums/StoreEnums";
import {Module, Action, Mutation, VuexModule} from "vuex-module-decorators";

export interface User {
  name: string;
  surname: string;
  email: string;
  password: string;
  access_token: string;
  refresh_token: string;
}

export interface UserAuthInfo {
  errors: Array<string>;
  user: User;
  isAuthenticated: boolean;
}

@Module
export default class AuthModule extends VuexModule implements UserAuthInfo {
  errors = [];
  user = {} as User;
  isAuthenticated = !!JwtService.getToken();

  /**
   * Get current user object
   * @returns User
   */
  get currentUser(): User {
    return this.user;
  }

  /**
   * Verify user authentication
   * @returns boolean
   */
  get isUserAuthenticated(): boolean {
    return this.isAuthenticated;
  }

  /**
   * Get authentification errors
   * @returns array
   */
  get getErrors(): Array<string> {
    return this.errors;
  }

  @Mutation
  [Mutations.SET_ERROR](error) {
    this.errors = error;
  }

  @Mutation
  [Mutations.SET_AUTH](user) {
    this.isAuthenticated = true;
    this.user = user;
    this.errors = [];

    const rememberMe = user.rememberMe === true;

    JwtService.saveToken(this.user.access_token, rememberMe);
    JwtService.saveRefreshToken(this.user.refresh_token, rememberMe);
  }

  @Mutation
  [Mutations.SET_USER](user) {
    this.user = user;
  }

  @Mutation
  [Mutations.SET_PASSWORD](password) {
    this.user.password = password;
  }

  @Mutation
  [Mutations.PURGE_AUTH]() {
    this.isAuthenticated = false;
    this.user = {} as User;
    this.errors = [];
    JwtService.destroyToken();
  }

  @Action
  [Actions.LOGIN](credentials) {
    return new Promise<void>((resolve, reject) => {
      const rememberMe = credentials.get("rememberMe") === "1";

      ApiService.post("login", credentials)
        .then(({data}) => {
          data.rememberMe = rememberMe;

          this.context.commit(Mutations.SET_AUTH, data);

          resolve();
        })
        .catch(({response}) => {
          this.context.commit(Mutations.SET_ERROR, response.data.detail);
          reject();
        });
    });
  }

  @Action
  [Actions.LOGOUT]() {
    this.context.commit(Mutations.PURGE_AUTH);
  }

  @Action
  [Actions.REGISTER](credentials) {
    return new Promise<void>((resolve, reject) => {
      ApiService.post("registration", credentials)
        .then(({data}) => {
          this.context.commit(Mutations.SET_AUTH, data);
        })
        .catch(({response}) => {
          this.context.commit(Mutations.SET_ERROR, response.data.errors);
          reject();
        });
    });
  }

  @Action
  [Actions.FORGOT_PASSWORD](payload) {
    return new Promise<void>((resolve, reject) => {
      ApiService.post("forgot_password", payload)
        .then(({data}) => {
          this.context.commit(Mutations.SET_AUTH, data);
          resolve();
        })
        .catch(({response}) => {
          this.context.commit(Mutations.SET_ERROR, response.data.detail);
          reject();
        });
    });
  }

  @Action
  [Actions.VERIFY_AUTH]() {
    return new Promise<void>((resolve, reject) => {
      if (JwtService.getToken()) {
        ApiService.setHeader();
        ApiService.get("verify")
          .then(({data}) => {
            // console.log(data)
            // this.context.commit(Mutations.SET_AUTH, data);
            resolve(data);
          })
          .catch(({response}) => {
            this.context.commit(Mutations.SET_ERROR, response.data.detail);
            reject(response);
          });
      } else {
        this.context.commit(Mutations.SET_ERROR, "Not Authenticated");
        reject({detail: "No token"});
      }
    });
  }

  @Action
  [Actions.UPDATE_USER](payload) {
    ApiService.setHeader();
    return new Promise<void>((resolve, reject) => {
      ApiService.post("update_user", payload)
        .then(({data}) => {
          this.context.commit(Mutations.SET_USER, data);
          resolve();
        })
        .catch(({response}) => {
          this.context.commit(Mutations.SET_ERROR, response.data.errors);
          reject();
        });
    });
  }
}
